import { useEffect, useState } from 'react';
import { checkForUpdate } from '../lib/versionCheck';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const RELOAD_COUNTDOWN_SECS = 10;

/**
 * Polls /version.json periodically and on tab refocus. When a newer version
 * is detected, shows a non-blocking toast offering an immediate reload (or
 * a 10-second auto-reload so users who walk away from their desk still pick
 * up the new version).
 */
export function VersionUpdateToast(): React.ReactElement | null {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [countdown, setCountdown] = useState(RELOAD_COUNTDOWN_SECS);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const stale = await checkForUpdate(__APP_VERSION__);
      if (!cancelled && stale) setUpdateAvailable(true);
    };

    void check();
    const interval = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    if (!updateAvailable) return;
    if (countdown <= 0) {
      window.location.reload();
      return;
    }
    const timer = window.setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [updateAvailable, countdown]);

  if (!updateAvailable) return null;

  // Mobile shell has a fixed tab bar (~4rem + safe-area). Lift the toast so it
  // does not cover tabs. Desktop keeps bottom-4.
  return (
    <div className="fixed right-4 z-[9999] max-w-sm rounded-lg border border-dark-border bg-dark-surface px-4 py-3 shadow-2xl bottom-4 max-[768px]:bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))]">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-white">New version available</p>
          <p className="mt-1 text-xs text-gray-400">
            Reloading in {countdown}s to pick up the latest changes.
          </p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="rounded border border-dark-border bg-dark-surface-light px-2 py-1 text-xs font-medium text-white hover:border-blue-400/50"
        >
          Reload now
        </button>
      </div>
    </div>
  );
}
